import { formatBrl } from '@estac1/shared';
import { Card, CardContent, Skeleton } from '@estac1/ui';
import type { UseQueryResult } from '@tanstack/react-query';
import type { SmsCostResponse } from './types.js';
import { smsCostTone } from './utils.js';

interface SmsCostSectionProps {
  smsCost: UseQueryResult<SmsCostResponse>;
}

export const SmsCostSection = ({ smsCost }: SmsCostSectionProps) => (
  <section className="space-y-3">
    <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
      <h2 className="text-sm font-semibold">Custo de SMS por estacionamento</h2>
      {smsCost.data && (
        <span className="text-xs text-muted-fg">
          Estimativa a {formatBrl(smsCost.data.unitCostCents)}/SMS · total{' '}
          {formatBrl(smsCost.data.totalSmsCostCents)}
        </span>
      )}
    </div>
    <p className="text-xs text-muted-fg">
      A receita cresce por vaga; o custo de SMS cresce por ticket. Lotes com custo alto frente à
      mensalidade (≥ 100% = margem negativa) aparecem no topo.
    </p>
    {smsCost.isLoading && <Skeleton className="h-40 w-full" />}
    {smsCost.data && smsCost.data.lots.length === 0 && (
      <p className="text-sm text-muted-fg">Nenhum SMS enviado no período.</p>
    )}
    {smsCost.data && smsCost.data.lots.length > 0 && (
      <Card>
        <CardContent className="p-0">
          <ul className="divide-y divide-border md:hidden">
            {smsCost.data.lots.map((row) => {
              const { negative, heavy } = smsCostTone(row.costPercentOfMrr);
              return (
                <li key={row.lotId} className="space-y-1 px-4 py-3 text-sm">
                  <p className="font-medium">{row.lotName}</p>
                  <p className="text-xs text-muted-fg tabular-nums">
                    {row.smsSent} SMS · {formatBrl(row.smsCostCents)}
                    {row.mrrCents != null ? ` · MRR ${formatBrl(row.mrrCents)}` : ''}
                  </p>
                  <p
                    className={`text-xs font-semibold tabular-nums ${
                      negative ? 'text-danger' : heavy ? 'text-warning' : 'text-muted-fg'
                    }`}
                  >
                    {row.costPercentOfMrr != null ? `${row.costPercentOfMrr}% do MRR` : '—'}
                  </p>
                </li>
              );
            })}
          </ul>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-fg">
                  <th className="px-4 py-3 font-medium">Estacionamento</th>
                  <th className="px-4 py-3 text-right font-medium">SMS</th>
                  <th className="px-4 py-3 text-right font-medium">Custo</th>
                  <th className="px-4 py-3 text-right font-medium">MRR</th>
                  <th className="px-4 py-3 text-right font-medium">% do MRR</th>
                </tr>
              </thead>
              <tbody>
                {smsCost.data.lots.map((row) => {
                  const { negative, heavy } = smsCostTone(row.costPercentOfMrr);
                  return (
                    <tr key={row.lotId} className="border-b border-border last:border-0">
                      <td className="px-4 py-3">{row.lotName}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{row.smsSent}</td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {formatBrl(row.smsCostCents)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {row.mrrCents != null ? formatBrl(row.mrrCents) : '—'}
                      </td>
                      <td
                        className={`px-4 py-3 text-right font-semibold tabular-nums ${
                          negative ? 'text-danger' : heavy ? 'text-warning' : ''
                        }`}
                      >
                        {row.costPercentOfMrr != null ? `${row.costPercentOfMrr}%` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    )}
  </section>
);
